import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Rocket as A1, DollarSign as A2, Lightbulb as A3, Users as A4, FileText as A5, TrendingUp as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import PathFitDashboard from '../components/paths/PathFitDashboard';
import { Button } from '../components/ui/button';
import { 
  Rocket, Lightbulb, DollarSign, Users, BookOpen, ExternalLink, Plus,
  TrendingUp, Target, Zap, CheckCircle, Star, Award, Globe,
  Briefcase, PiggyBank, Megaphone, Code, ShoppingBag, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Entrepreneurship Paths
const ENTREPRENEUR_PATHS = [
  {
    id: 'tech_startup',
    title: 'Tech Startup Founder',
    icon: Code,
    color: 'blue',
    description: 'Build software, apps, or tech products that solve problems at scale.',
    skills: ['Programming/No-Code', 'Product Management', 'Fundraising', 'Leadership', 'Sales'],
    examples: ['SaaS Company', 'Mobile App', 'AI Startup', 'Fintech', 'EdTech'],
    funding: 'Venture Capital, Angel Investors, Accelerators',
    timeline: '2-5 years to significant traction',
    resources: [
      { name: 'Y Combinator Startup School', url: 'https://www.startupschool.org', free: true },
      { name: 'TechStars', url: 'https://www.techstars.com', free: false },
    ],
  },
  {
    id: 'ecommerce',
    title: 'E-Commerce Business',
    icon: ShoppingBag,
    color: 'emerald',
    description: 'Sell products online through your own store or marketplaces like Amazon.',
    skills: ['Product Sourcing', 'Marketing', 'Customer Service', 'Inventory Management', 'Analytics'],
    examples: ['Dropshipping', 'Private Label', 'Handmade Products', 'Print-on-Demand'],
    funding: 'Self-funded, Small Business Loans',
    timeline: '3-12 months to first sales',
    resources: [
      { name: 'Shopify Learn', url: 'https://www.shopify.com/learn', free: true },
      { name: 'Amazon FBA', url: 'https://sell.amazon.com', free: false },
    ],
  },
  {
    id: 'content_creator',
    title: 'Content Creator / Influencer',
    icon: Megaphone,
    color: 'pink',
    description: 'Build an audience and monetize through sponsorships, products, and ads.',
    skills: ['Content Creation', 'Video Editing', 'Social Media', 'Personal Branding', 'Negotiation'],
    examples: ['YouTube', 'TikTok', 'Instagram', 'Podcast', 'Newsletter'],
    funding: 'Self-funded initially, Brand deals',
    timeline: '6-24 months to monetization',
    resources: [
      { name: 'YouTube Creator Academy', url: 'https://creatoracademy.youtube.com', free: true },
      { name: 'Creator Economy Newsletter', url: 'https://www.thecreatoreconomy.com', free: true },
    ],
  },
  {
    id: 'freelance',
    title: 'Freelance / Consulting',
    icon: Briefcase,
    color: 'purple',
    description: 'Offer your skills as services to clients. Be your own boss.',
    skills: ['Expertise in a Field', 'Client Management', 'Pricing Strategy', 'Time Management', 'Networking'],
    examples: ['Web Development', 'Design', 'Writing', 'Marketing', 'Coaching'],
    funding: 'No funding needed',
    timeline: 'Can start immediately',
    resources: [
      { name: 'Upwork', url: 'https://www.upwork.com', free: true },
      { name: 'Toptal', url: 'https://www.toptal.com', free: true },
    ],
  },
  {
    id: 'local_business',
    title: 'Local / Service Business',
    icon: Users,
    color: 'amber',
    description: 'Start a business serving your local community - restaurants, services, retail.',
    skills: ['Operations', 'Customer Service', 'Local Marketing', 'Hiring', 'Financial Management'],
    examples: ['Restaurant', 'Cleaning Service', 'Tutoring', 'Landscaping', 'Fitness Studio'],
    funding: 'SBA Loans, Personal Savings, Family',
    timeline: '3-12 months to launch',
    resources: [
      { name: 'SBA.gov', url: 'https://www.sba.gov', free: true },
      { name: 'SCORE Mentorship', url: 'https://www.score.org', free: true },
    ],
  },
  {
    id: 'creative',
    title: 'Creative Business',
    icon: Palette,
    color: 'cyan',
    description: 'Turn your creative passion into a business - art, music, design, crafts.',
    skills: ['Creative Skills', 'Marketing', 'E-commerce', 'Branding', 'Client Relations'],
    examples: ['Graphic Design', 'Photography', 'Music Production', 'Crafts/Etsy', 'Art Sales'],
    funding: 'Self-funded, Grants',
    timeline: '1-6 months to first clients',
    resources: [
      { name: 'Etsy Seller Handbook', url: 'https://www.etsy.com/seller-handbook', free: true },
      { name: 'Creative Market', url: 'https://creativemarket.com', free: false },
    ],
  },
];

// Startup Resources
const STARTUP_RESOURCES = [
  { name: 'Y Combinator Startup School', url: 'https://www.startupschool.org', desc: 'Free online course from top accelerator', type: 'Course' },
  { name: 'How to Start a Startup (Stanford)', url: 'https://startupclass.samaltman.com', desc: 'Legendary lecture series', type: 'Course' },
  { name: 'The Lean Startup', url: 'https://www.amazon.com/Lean-Startup-Entrepreneurs-Continuous-Innovation/dp/0307887898', desc: 'Essential reading for founders', type: 'Book' },
  { name: 'Zero to One', url: 'https://www.amazon.com/Zero-One-Notes-Startups-Future/dp/0804139296', desc: 'Peter Thiel on building startups', type: 'Book' },
  { name: 'Indie Hackers', url: 'https://www.indiehackers.com', desc: 'Community of bootstrapped founders', type: 'Community' },
  { name: 'Product Hunt', url: 'https://www.producthunt.com', desc: 'Launch and discover products', type: 'Platform' },
];

// Young Entrepreneur Programs
const YOUTH_PROGRAMS = [
  { name: 'NFTE (Network for Teaching Entrepreneurship)', url: 'https://www.nfte.com', desc: 'Entrepreneurship education for youth', grades: 'Middle & High School' },
  { name: 'DECA Entrepreneurship', url: 'https://www.deca.org', desc: 'Business competitions and training', grades: 'High School' },
  { name: 'Junior Achievement', url: 'https://www.ja.org', desc: 'Financial literacy and entrepreneurship', grades: 'K-12' },
  { name: 'Kantner Foundation Young Entrepreneurs', url: 'https://www.kantnerfoundation.org', desc: 'Grants for young entrepreneurs', grades: 'High School' },
  { name: 'Diamond Challenge', url: 'https://diamondchallenge.org', desc: 'Global entrepreneurship competition', grades: 'High School' },
  { name: 'Conrad Challenge', url: 'https://www.conradchallenge.org', desc: 'Innovation competition', grades: 'High School' },
];

export default function EntrepreneurshipPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [selectedPath, setSelectedPath] = useState(null);
  const [activeTab, setActiveTab] = useState('paths');

  const addToGoals = async (item, category = 'career') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item.title || item.name || item,
        description: item.description || item.desc || 'Entrepreneurship goal',
        category: category,
        priority: 'medium',
        status: 'backlog',
        source: 'Entrepreneurship'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="entrepreneurship-arrival"
        eyebrow="Entrepreneurship"
        title="Build it with real numbers, not hope."
        subtitle="Most businesses don't fail because the idea was bad. They fail because nobody priced the first eighteen months. Here you'll shape the idea, find out what it costs to run, where the money can come from, and what you'd need to charge to make it worth doing."
        accent="#f472b6"
        second="#a78bfa"
        ctaLabel="Start building"
        narration={"This is Entrepreneurship. We're not here to hype you up, we're here to make the numbers real. You'll shape what the business actually is, work out what it costs to start and to run, see the funding that exists for founders like you, and find out what you'd have to sell each month to cover your own life. Then you'll know whether to go, or what has to change first."}
        facts={[
          { icon: A2, head: 'What it really costs', sub: 'Start-up, monthly burn, and the first eighteen months' },
          { icon: A6, head: 'What you must charge', sub: 'Priced against the income your life takes' },
          { icon: A4, head: 'Who helps for free', sub: 'SCORE, SBA, Boots to Business and local partners' },
        ]}
        modulesTitle="What you'll work through"
        modules={[
          { icon: A3, label: 'Shape the idea', line: 'Who it serves, what they pay for, and what makes you the one to do it.', hue: '#fbbf24' },
          { icon: A2, label: 'Money in, money out', line: 'Start-up costs, running costs, break-even and the runway you need.', hue: '#34d399' },
          { icon: A5, label: 'Make it legal', line: 'Structure, registration, licences, insurance and the paperwork order.', hue: '#60a5fa' },
          { icon: A4, label: 'Funding and mentors', line: 'Grants, microloans, SBA routes and free advisers who do this all day.', hue: '#a78bfa' },
          { icon: A6, label: 'First customers', line: 'How to test demand before you spend money you cannot lose.', hue: '#22d3ee' },
          { icon: A1, label: 'Go or wait', line: 'An honest verdict, and what would have to be true to change it.', hue: '#f472b6' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Entrepreneurship"
        description="Build something of your own, solve a problem, own the upside, choose your own hours."
      />

      {/* Fit dashboard, is entrepreneurship a good match? */}
      <PathFitDashboard
        pathLabel="entrepreneurship"
        fit={{ overall: 71, career: 82, financial: 55, timeline: 68 }}
        reasons={['You value autonomy and creative work', 'Low starting capital for service businesses', 'Upside uncapped', 'Real income takes 2-3 years typically']}
      />

      {/* Hero Banner, brand cyan/violet with glow */}
      <div className="mb-6 rounded-2xl border border-cyan-400/25 bg-[#0a1729]/60 p-5"
        style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.55)' }}>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 shrink-0 rounded-2xl border border-violet-400/40 bg-violet-500/10 flex items-center justify-center"
            style={{ boxShadow: '0 0 22px -8px rgba(167,139,250,0.6)' }}>
            <Rocket className="w-8 h-8 text-violet-300" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl font-semibold text-white mb-1">Why entrepreneurship?</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              Be your own boss. Solve problems you care about. Build wealth on your own terms,               tech startup, local service, or a creator business.
            </p>
          </div>
          <div className="text-center shrink-0">
            <p className="text-3xl font-bold bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">62%</p>
            <p className="text-xs text-slate-300">of billionaires are<br/>self-made</p>
          </div>
        </div>
      </div>

      {/* Quick Stats, 2024 SBA / Census figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="text-center">
          <Lightbulb className="w-6 h-6 mx-auto mb-2 text-cyan-300" />
          <p className="text-2xl font-bold text-white">34.7M</p>
          <p className="text-xs text-slate-300">Small businesses in the U.S. (SBA 2024)</p>
        </GlassCard>
        <GlassCard className="text-center">
          <DollarSign className="w-6 h-6 mx-auto mb-2 text-violet-300" />
          <p className="text-2xl font-bold text-white">$0</p>
          <p className="text-xs text-slate-300">To start a service business</p>
        </GlassCard>
        <GlassCard className="text-center">
          <TrendingUp className="w-6 h-6 mx-auto mb-2 text-sky-300" />
          <p className="text-2xl font-bold text-white">5.5M</p>
          <p className="text-xs text-slate-300">New business applications (2024)</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Users className="w-6 h-6 mx-auto mb-2 text-pink-300" />
          <p className="text-2xl font-bold text-white">76M</p>
          <p className="text-xs text-slate-300">U.S. freelancers (Upwork 2025)</p>
        </GlassCard>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'paths', label: 'Business Types', icon: Rocket },
          { id: 'resources', label: 'Learning Resources', icon: BookOpen },
          { id: 'programs', label: 'Youth Programs', icon: Award },
          { id: 'steps', label: 'Getting Started', icon: Target },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-300/60 bg-cyan-300/15 text-white'
                  : 'border-white/12 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Business Types Tab */}
      {activeTab === 'paths' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ENTREPRENEUR_PATHS.map(path => {
            const Icon = path.icon;
            const isExpanded = selectedPath?.id === path.id;
            
            return (
              <GlassCard 
                key={path.id}
                className={`cursor-pointer transition-all hover:scale-[1.02] ${
                  isExpanded ? 'ring-2 ring-amber-500' : ''
                }`}
                onClick={() => setSelectedPath(isExpanded ? null : path)}
              >
                <div className={`w-12 h-12 bg-${path.color}-500/20 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 text-${path.color}-400`} />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-1">{path.title}</h3>
                <p className="text-slate-300 text-sm mb-4">{path.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {path.examples.slice(0, 3).map(ex => (
                    <span key={ex} className="px-2 py-1 bg-navy-700 text-slate-300 text-xs rounded-full">
                      {ex}
                    </span>
                  ))}
                </div>
                
                <div className="text-sm text-slate-300">
                  <span className="text-emerald-400">{path.timeline}</span>
                </div>
                
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-navy-700 space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Skills Needed:</h4>
                      <div className="flex flex-wrap gap-2">
                        {path.skills.map(skill => (
                          <span key={skill} className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Funding Options:</h4>
                      <p className="text-sm text-slate-300">{path.funding}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Resources:</h4>
                      {path.resources.map(resource => (
                        <a
                          key={resource.name}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-between p-2 bg-navy-900/50 rounded-lg hover:bg-navy-800 mb-2"
                        >
                          <span className="text-sm text-white">{resource.name}</span>
                          <div className="flex items-center gap-2">
                            {resource.free && <span className="text-xs text-emerald-400">FREE</span>}
                            <ExternalLink className="w-4 h-4 text-blue-400" />
                          </div>
                        </a>
                      ))}
                    </div>
                    
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToGoals(path);
                      }}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add to My Goals
                    </Button>
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <BookOpen className="w-10 h-10 text-amber-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-4">Essential Resources</h3>
            <div className="space-y-3">
              {STARTUP_RESOURCES.map(resource => (
                <a
                  key={resource.name}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{resource.name}</span>
                    <span className="text-slate-300 text-sm block">{resource.desc}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-navy-700 text-slate-300 rounded-full">{resource.type}</span>
                    <ExternalLink className="w-4 h-4 text-blue-400" />
                  </div>
                </a>
              ))}
            </div>
          </GlassCard>
          
          <GlassCard>
            <Globe className="w-10 h-10 text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-4">Startup Accelerators</h3>
            <div className="space-y-3">
              {[
                { name: 'Y Combinator', url: 'https://www.ycombinator.com', desc: 'Most famous accelerator - $500k investment', acceptance: '1.5%' },
                { name: 'Techstars', url: 'https://www.techstars.com', desc: 'Global network, $120k investment', acceptance: '1%' },
                { name: 'MassChallenge', url: 'https://masschallenge.org', desc: 'No equity taken, great for early stage', acceptance: '10%' },
                { name: '500 Global', url: 'https://500.co', desc: 'Early-stage VC and accelerator', acceptance: '2%' },
              ].map(acc => (
                <a
                  key={acc.name}
                  href={acc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{acc.name}</span>
                    <span className="text-slate-300 text-sm block">{acc.desc}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-amber-400">{acc.acceptance} accept</span>
                    <ExternalLink className="w-4 h-4 text-blue-400 ml-2 inline" />
                  </div>
                </a>
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {/* Youth Programs Tab */}
      {activeTab === 'programs' && (
        <div className="space-y-6">
          <GlassCard>
            <Award className="w-10 h-10 text-purple-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Entrepreneurship Programs for Students</h3>
            <p className="text-slate-300 mb-6">Get a head start on your entrepreneurial journey with these programs designed for young founders.</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {YOUTH_PROGRAMS.map(program => (
                <div key={program.name} className="p-4 bg-navy-900/50 rounded-lg border border-navy-700 hover:border-purple-500/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-white font-semibold">{program.name}</h4>
                      <p className="text-slate-300 text-sm mt-1">{program.desc}</p>
                      <span className="text-xs text-purple-400 mt-2 inline-block">{program.grades}</span>
                    </div>
                    <a
                      href={program.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addToGoals({ title: `Apply to ${program.name}`, description: program.desc })}
                    className="mt-3 bg-purple-600 hover:bg-purple-700 w-full"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add to Goals
                  </Button>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {/* Getting Started Tab */}
      {activeTab === 'steps' && (
        <GlassCard>
          <Target className="w-10 h-10 text-emerald-400 mb-4" />
          <h3 className="text-xl font-bold text-white mb-6">How to Get Started</h3>
          
          <div className="space-y-6">
            {[
              { step: 1, title: 'Find a Problem to Solve', desc: 'Look for frustrations in your life or others. The best businesses solve real problems people will pay to fix.', action: 'Brainstorm 10 problems' },
              { step: 2, title: 'Validate Your Idea', desc: "Talk to potential customers before building anything. Would they pay for your solution? Don't skip this step!", action: 'Interview 10 potential customers' },
              { step: 3, title: 'Start Small (MVP)', desc: 'Build the simplest version of your product. Launch fast, get feedback, iterate. Perfect is the enemy of done.', action: 'Define your MVP' },
              { step: 4, title: 'Get Your First Customer', desc: 'One paying customer proves your concept. Focus on making them incredibly happy before scaling.', action: 'Land first customer' },
              { step: 5, title: 'Learn & Iterate', desc: 'Listen to feedback, improve your product, and grow. Entrepreneurship is a journey of continuous learning.', action: 'Set up feedback system' },
            ].map((item, index) => (
              <div key={item.step} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  {index < 4 && <div className="w-0.5 h-full bg-emerald-600/30 mt-2" />}
                </div>
                <div className="flex-1 pb-6">
                  <h4 className="text-white font-semibold text-lg">{item.title}</h4>
                  <p className="text-slate-300 mt-1">{item.desc}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addToGoals({ title: item.action, description: item.desc })}
                    className="mt-3 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {item.action}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </AppLayout>
  );
}
